import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  ChevronUp,
  Coins,
  Gift,
  Hourglass,
  PackageCheck,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { CHAT_MESSAGE_COST, type ChatMessageDto } from "@mandoob/shared";

import {
  getGetCountsQueryKey,
  getThread,
  useCloseConversation,
  useGetThread,
  useMarkRead,
  useReactToMessage,
  useSendMessage,
  useSendTemplate,
  useUpdateDelivery,
} from "@/api/inbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { errorMessage } from "@/lib/errors";
import { hapticImpact, hapticNotification } from "@/lib/haptics";
import { formatPhoneNumberForDisplay } from "@/lib/phone-utils";
import { cn, getErrorCode } from "@/lib/utils";
import { loadSession, useSession } from "@/session";

import {
  ChatComposer,
  type ChatComposerHandle,
  type ComposerSendPayload,
} from "./chat-composer";
import { ConversationAvatar } from "./conversation-avatar";
import { DeliveryDetailsSheet } from "./delivery-details-sheet";
import { DeliveryStatusBadge } from "./delivery-status-badge";
import { formatDayDivider, isConversationArchived } from "./lib";
import { MessageBubble } from "./message-bubble";
import { SessionCountdown } from "./session-pill";
import { TemplatePickerDialog } from "./template-picker-dialog";

const THREAD_PAGE_SIZE = 100;

function dedupeById(messages: ChatMessageDto[]): ChatMessageDto[] {
  const seen = new Set<string>();
  const out: ChatMessageDto[] = [];
  for (const message of messages) {
    if (seen.has(message.id)) continue;
    seen.add(message.id);
    out.push(message);
  }
  return out;
}

export function ChatThreadPage({ conversationId }: { conversationId: string }) {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();

  // Chat messages cost CHAT_MESSAGE_COST each (the API enforces it; this only
  // shapes the UI).
  const creditsBalance = session?.driver.creditsBalance ?? 0;
  const creditsExpired =
    !!session?.driver.creditsExpiresAt &&
    Date.parse(session.driver.creditsExpiresAt) <= Date.now();
  // Native app with DISABLE_APP_PAYMENTS: no purchase links (store policy).
  const paymentsHidden = !!session?.appPaymentsDisabled && isNative();
  const rechargeAction = paymentsHidden
    ? undefined
    : {
        label: t("Mandoob:Plans.Buy"),
        onClick: () => void navigate({ to: "/plans" }),
      };

  const threadQuery = useGetThread(conversationId, {
    MaxResultCount: THREAD_PAGE_SIZE,
  });
  const thread = threadQuery.data;
  // Free-messages offer: nothing to afford while the chat still has free
  // sends left (the API decides; this only shapes the UI).
  const freeMessagesLeft = thread?.freeMessagesLeft ?? 0;
  const cannotAffordMessage =
    freeMessagesLeft <= 0 &&
    (creditsExpired || creditsBalance < CHAT_MESSAGE_COST);

  // Older pages loaded on demand ("load older" button, v1 keeps it simple).
  const [older, setOlder] = useState<ChatMessageDto[]>([]);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Optimistic outbox (reference behavior): a sent message appears instantly
  // with the pending clock tick; the server copy replaces it on success, a
  // failed send flips it to the Failed state.
  const [outbox, setOutbox] = useState<ChatMessageDto[]>([]);
  useEffect(() => setOutbox([]), [conversationId]);
  const serverMessages = thread?.messages;
  useEffect(() => {
    // Prune local copies the server list now carries.
    if (!serverMessages?.length) return;
    const ids = new Set(serverMessages.map((m) => m.id));
    setOutbox((prev) => prev.filter((m) => !ids.has(m.id)));
  }, [serverMessages]);

  const messages = useMemo(
    () => dedupeById([...older, ...(thread?.messages ?? []), ...outbox]),
    [older, thread?.messages, outbox],
  );

  const totalCount = thread?.totalMessageCount ?? 0;
  const hasOlder = messages.length < totalCount;

  const loadOlder = async () => {
    if (loadingOlder || !thread) return;
    setLoadingOlder(true);
    try {
      const page = await getThread(conversationId, {
        SkipCount: messages.length,
        MaxResultCount: THREAD_PAGE_SIZE,
      });
      setOlder((prev) => dedupeById([...page.messages, ...prev]));
    } catch (error) {
      toast.error(errorMessage(error, t));
    } finally {
      setLoadingOlder(false);
    }
  };

  // Mark the conversation read on mount and whenever new messages arrive.
  const markRead = useMarkRead();
  const unreadCount = thread?.unreadCount ?? 0;
  useEffect(() => {
    if (!thread || unreadCount === 0) return;
    markRead.mutate(
      { conversationId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: ["/api/inbox/conversations"],
          });
          void queryClient.invalidateQueries({
            queryKey: getGetCountsQueryKey(),
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, unreadCount, thread?.lastMessageAt]);

  // Paged-in history belongs to one conversation — reset it on switch (the
  // component stays mounted in the desktop split view).
  useEffect(() => setOlder([]), [conversationId]);

  const listRef = useRef<HTMLDivElement>(null);
  const listContentRef = useRef<HTMLDivElement>(null);
  // True while the driver is at (or near) the newest message — the list then
  // STAYS pinned to the bottom even as media loads in and grows the content.
  const pinnedRef = useRef(true);
  const snapToEnd = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const handleListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };

  // Snap to the newest message on open/switch and when new messages arrive.
  const newestId = messages.length
    ? messages[messages.length - 1].id
    : undefined;
  useEffect(() => {
    pinnedRef.current = true;
    snapToEnd();
    const raf = requestAnimationFrame(snapToEnd);
    return () => cancelAnimationFrame(raf);
  }, [conversationId, newestId]);

  // Media (images, videos, link previews) loading in after first paint grows
  // the content and would shove the view up — while pinned, follow every
  // content-size change back to the bottom. Scrolling away unpins.
  const ready = !!thread;
  useEffect(() => {
    const content = listContentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) snapToEnd();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [ready]);

  // The composer overlays the list, so the list needs exactly its height as
  // bottom clearance — measured live, because the quick-actions bar, reply
  // preview and link preview all change it. While pinned, follow growth so
  // the newest message stays above the pill.
  const footerRef = useRef<HTMLElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setFooterHeight(el.offsetHeight);
      if (pinnedRef.current) requestAnimationFrame(snapToEnd);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ready]);

  const invalidateThread = () => {
    void queryClient.invalidateQueries({
      queryKey: [`/api/inbox/conversations/${conversationId}/thread`],
    });
    void queryClient.invalidateQueries({
      queryKey: ["/api/inbox/conversations"],
    });
  };

  const sendMessage = useSendMessage({
    mutation: {
      onSuccess: () => {
        // Confirms the message actually left, which the optimistic-looking UI
        // otherwise only says visually.
        hapticImpact("light");
        invalidateThread();
        // The send debited a credit — refresh the session's balance.
        void loadSession().catch(() => undefined);
      },
      onError: (error) => {
        hapticNotification("error");
        const code = getErrorCode(error);
        if (code === "Waba:WindowClosed") {
          toast.error(t("Mandoob:Thread.WindowClosedToast"));
          invalidateThread();
          return;
        }
        if (code === "Credits:Insufficient" || code === "Credits:Expired") {
          // The balance shown was stale (spent on another device or expired):
          // re-sync it so the composer swaps to the out-of-credits state.
          toast.error(errorMessage(error, t), { action: rechargeAction });
          void loadSession().catch(() => undefined);
          return;
        }
        toast.error(errorMessage(error, t));
      },
    },
  });

  // Fire-and-forget: append a pending local bubble immediately (WhatsApp
  // style), then let the mutation replace it with the server row or flip it
  // to Failed. The mutation config above still owns the toasts/invalidation.
  const handleComposerSend = (payload: ComposerSendPayload) => {
    const reply = replyTo;
    setReplyTo(null);
    const localId = `local-${crypto.randomUUID()}`;
    const now = new Date();
    const local: ChatMessageDto = {
      id: localId,
      conversationId,
      wamid: null,
      direction: "Outbound",
      type: payload.location ? "Location" : (payload.mediaKind ?? "Text"),
      body: payload.location
        ? JSON.stringify(payload.location)
        : (payload.body ?? null),
      mediaUrl: null,
      mediaMimeType: null,
      mediaFileName: payload.mediaFileName ?? null,
      templateName: null,
      waTimestamp: Math.floor(now.getTime() / 1000),
      deliveryTime: null,
      readTime: null,
      creationTime: now.toISOString(),
      delivery: "Pending",
      errorCode: null,
      replyToWamid: reply?.wamid ?? null,
      driverReaction: null,
      clientReaction: null,
    };
    setOutbox((prev) => [...prev, local]);
    sendMessage.mutate(
      {
        conversationId,
        ...payload,
        ...(reply?.wamid ? { replyToWamid: reply.wamid } : {}),
      },
      {
        onSuccess: (message) => {
          setOutbox((prev) =>
            prev.map((m) => (m.id === localId ? message : m)),
          );
        },
        onError: () => {
          setOutbox((prev) =>
            prev.map((m) =>
              m.id === localId ? { ...m, delivery: "Failed" as const } : m,
            ),
          );
        },
      },
    );
  };

  const reactToMessage = useReactToMessage({
    mutation: {
      onError: (error) => toast.error(errorMessage(error, t)),
    },
  });
  const handleReact = (message: ChatMessageDto, emoji: string) => {
    if (reactToMessage.isPending) return;
    reactToMessage.mutate({ messageId: message.id, emoji });
  };

  // Quoted reply: the message the next send replies to (double-click a row on
  // desktop, swipe the bubble on mobile, or the message menu's Reply item).
  const [replyTo, setReplyTo] = useState<ChatMessageDto | null>(null);
  useEffect(() => setReplyTo(null), [conversationId]);

  // The overlaid composer grows when the reply preview shows; if the driver
  // was already at the bottom, keep the newest message pinned above it.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom) requestAnimationFrame(snapToEnd);
  }, [replyTo]);

  const composerRef = useRef<ChatComposerHandle>(null);
  const startReply = (message: ChatMessageDto) => {
    setReplyTo(message);
    // Next frame: when Reply comes from the message menu, the menu is still
    // closing and would otherwise contest the focus.
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  // Put the caret in the composer as soon as an open chat is on screen —
  // desktop only. On phones focusing pops the soft keyboard, and Android's
  // back gesture then dismisses the keyboard first, making leaving the chat
  // take two swipes.
  const windowOpen = thread?.windowOpen ?? false;
  useEffect(() => {
    if (!windowOpen) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    composerRef.current?.focus();
  }, [conversationId, windowOpen]);

  // Resolves a message's replyToWamid to the quoted message, when loaded.
  const byWamid = useMemo(() => {
    const map = new Map<string, ChatMessageDto>();
    for (const m of messages) if (m.wamid) map.set(m.wamid, m);
    return map;
  }, [messages]);

  // Tapping a quote scrolls to (and briefly highlights) the quoted message.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jumpToMessage = (messageId: string) => {
    document
      .getElementById(`msg-${messageId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(messageId);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 1200);
  };

  // One-tap delivered toggle in the top bar: marks the shipment Delivered,
  // or back to Ongoing when unmarked (invalidations come from the hook).
  const updateDelivery = useUpdateDelivery({
    mutation: {
      onSuccess: () => hapticNotification("success"),
      onError: (error) => toast.error(errorMessage(error, t)),
    },
  });
  const toggleDelivered = () => {
    if (!thread || updateDelivery.isPending) return;
    updateDelivery.mutate({
      conversationId,
      status: thread.deliveryStatus === "Delivered" ? "Ongoing" : "Delivered",
    });
  };

  // Re-close a reopened (finished) chat — the client number frees up again.
  const closeChat = useCloseConversation({
    mutation: {
      onSuccess: () => {
        hapticNotification("success");
        toast.success(t("Mandoob:Thread.ChatClosed"));
        invalidateThread();
        void queryClient.invalidateQueries({
          queryKey: getGetCountsQueryKey(),
        });
      },
      onError: (error) => toast.error(errorMessage(error, t)),
    },
  });

  const [templateOpen, setTemplateOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const sendTemplate = useSendTemplate({
    mutation: {
      onSuccess: () => {
        // A template send costs a credit — worth a firmer confirmation.
        hapticNotification("success");
        setTemplateOpen(false);
        invalidateThread();
        // The send debited a credit — refresh the session's balance.
        void loadSession().catch(() => undefined);
      },
      onError: (error) => {
        hapticNotification("error");
        if (getErrorCode(error) === "Credits:Insufficient") {
          toast.error(t("Mandoob:Credits.InsufficientToast"));
          return;
        }
        toast.error(errorMessage(error, t));
      },
    },
  });

  if (threadQuery.isLoading) {
    return (
      <div dir={dir} className="tw:flex tw:h-full tw:flex-col">
        <div className="tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-border tw:px-4 tw:pb-3 tw:pt-[calc(12px+env(safe-area-inset-top))]">
          <Skeleton className="tw:size-10 tw:rounded-full" />
          <div className="tw:flex tw:flex-col tw:gap-2">
            <Skeleton className="tw:h-4 tw:w-32" />
            <Skeleton className="tw:h-3 tw:w-24" />
          </div>
        </div>
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:p-4">
          <Skeleton className="tw:h-14 tw:w-2/3 tw:self-start tw:rounded-xl" />
          <Skeleton className="tw:h-14 tw:w-2/3 tw:self-end tw:rounded-xl" />
          <Skeleton className="tw:h-14 tw:w-1/2 tw:self-start tw:rounded-xl" />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div
        dir={dir}
        className="tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:p-8 tw:text-center"
      >
        <p className="tw:m-0 tw:text-lg tw:font-semibold">
          {t("Mandoob:Thread.NotFound")}
        </p>
        <Button asChild variant="outline">
          <Link to="/chats">{t("Mandoob:Back")}</Link>
        </Button>
      </div>
    );
  }

  // Same identity rules as the conversation row: the tracking number leads and
  // an unnamed client falls back to their phone number.
  const phone = formatPhoneNumberForDisplay(thread.phoneNumber);
  const client = thread.name.trim() || phone;
  const tracking = thread.trackingNumber?.trim();
  // Hidden when the headline is already the phone number.
  const showPhone = !!tracking || client !== phone;

  return (
    <div dir={dir} className="tw:flex tw:h-full tw:flex-col">
      <header className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:border-b tw:border-border tw:px-1.5 tw:pb-2 tw:pt-[calc(8px+env(safe-area-inset-top))] tw:md:gap-3 tw:md:px-3 tw:md:pb-3 tw:md:pt-[calc(12px+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={() => void navigate({ to: "/chats" })}
          aria-label={t("Mandoob:Back")}
          className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted tw:md:size-10"
        >
          <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
        </button>
        <button
          type="button"
          onClick={() => setDeliveryOpen(true)}
          aria-label={t("Mandoob:Delivery.DetailsTitle")}
          className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:rounded-xl tw:px-1 tw:py-1 tw:text-start tw:transition-colors tw:hover:bg-muted/60 tw:active:bg-muted tw:md:gap-3"
        >
          {/* Phones: the window countdown badge overlaps the avatar's bottom
              edge to keep the bar compact. Desktop keeps the pill at the end
              of the bar instead. */}
          <span className="tw:relative tw:shrink-0">
            <ConversationAvatar
              name={client}
              trackingNumber={thread.trackingNumber}
              phone={thread.phoneNumber}
              size={10}
            />
            {/* Phone: the delivery status overlaps the avatar (the countdown
                lives in the composer now); md+ shows the full badge inline. */}
            <span className="tw:absolute tw:-bottom-1.5 tw:left-1/2 tw:z-1 tw:-translate-x-1/2 tw:md:hidden">
              <DeliveryStatusBadge
                status={thread.deliveryStatus}
                className="tw:whitespace-nowrap tw:px-1.5 tw:py-px tw:text-[10px] tw:font-semibold tw:leading-[1.4]"
              />
            </span>
          </span>
          <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
            <span className="tw:truncate tw:text-base tw:font-semibold">
              <bdi>{tracking ?? client}</bdi>
            </span>
            {showPhone && (
              <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
                <bdi>{phone}</bdi>
              </span>
            )}
          </span>
          <DeliveryStatusBadge
            status={thread.deliveryStatus}
            className="tw:hidden tw:md:inline-flex"
          />
        </button>
        {/* The one action a delivery exists for, so it gets its own identity:
            a dashed "complete me" pill that fills solid green (with a glow
            ring) once the shipment is delivered. Tap again to unmark. Gone
            once the status is locked (cancelled shipment / archived chat). */}
        {thread.deliveryStatus === "Cancelled" ||
        isConversationArchived(thread) ? null : (
          <button
            type="button"
            onClick={toggleDelivered}
            disabled={updateDelivery.isPending}
            aria-label={t(
              thread.deliveryStatus === "Delivered"
                ? "Mandoob:Delivery.UnmarkDelivered"
                : "Mandoob:Delivery.MarkDelivered",
            )}
            title={t(
              thread.deliveryStatus === "Delivered"
                ? "Mandoob:Delivery.UnmarkDelivered"
                : "Mandoob:Delivery.MarkDelivered",
            )}
            className={cn(
              "tw:flex tw:h-9 tw:shrink-0 tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:px-2.5 tw:text-[13px] tw:font-semibold tw:transition-all tw:active:scale-95 tw:md:h-10 tw:md:gap-1.5 tw:md:px-3 tw:md:text-sm",
              thread.deliveryStatus === "Delivered"
                ? "tw:bg-success tw:text-white tw:ring-4 tw:ring-success/25"
                : "tw:border-2 tw:border-dashed tw:border-success tw:bg-success-soft tw:text-success-foreground tw:hover:bg-success/15",
            )}
          >
            <PackageCheck
              className="tw:size-4.5 tw:shrink-0 tw:md:size-5"
              strokeWidth={thread.deliveryStatus === "Delivered" ? 2.25 : 1.9}
            />
            <span>
              {t(
                thread.deliveryStatus === "Delivered"
                  ? "Mandoob:Delivery.StatusDelivered"
                  : "Mandoob:Delivery.Deliver",
              )}
            </span>
          </button>
        )}
        {/* A client message reopened this finished chat: the amber pill both
            labels the state and re-closes the chat (freeing the number). */}
        {thread.reopened ? (
          <button
            type="button"
            onClick={() => closeChat.mutate({ conversationId })}
            disabled={closeChat.isPending}
            aria-label={t("Mandoob:Thread.CloseChat")}
            title={t("Mandoob:Thread.CloseChat")}
            className="tw:flex tw:h-9 tw:shrink-0 tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:border tw:border-current tw:bg-warning-soft tw:px-2.5 tw:text-[13px] tw:font-semibold tw:text-warning tw:transition-all tw:active:scale-95 tw:md:h-10 tw:md:gap-1.5 tw:md:px-3 tw:md:text-sm"
          >
            <span>{t("Mandoob:Chats.Reopened")}</span>
            <X className="tw:size-4 tw:shrink-0" strokeWidth={2.25} />
          </button>
        ) : null}
      </header>

      <div className="tw:relative tw:min-h-0 tw:flex-1">
        <div
          ref={listRef}
          onScroll={handleListScroll}
          // Bottom padding clears the overlaid composer, so the last message
          // rests above it while scrolled messages pass visibly underneath.
          // The clearance is the composer footer's MEASURED height (plus a
          // little air), so the quick-actions bar, reply preview and link
          // preview are all compensated exactly. Horizontal padding lives on
          // each message row (not here) so the long-press/flash row
          // highlights span edge-to-edge.
          className="chat-wallpaper tw:h-full tw:overflow-y-auto tw:pt-4"
          style={{ paddingBottom: (footerHeight || 88) + 8 }}
        >
          {/* Inner wrapper: the ResizeObserver watches its height so the list
              stays pinned to the bottom while media loads in. */}
          <div ref={listContentRef} className="tw:flex tw:flex-col tw:gap-2">
            {hasOlder && (
              <div className="tw:flex tw:justify-center tw:pb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void loadOlder()}
                  loading={loadingOlder}
                  className="tw:rounded-full"
                >
                  <ChevronUp className="tw:size-4" />
                  {t("Mandoob:Thread.LoadOlder")}
                </Button>
              </div>
            )}
            {messages.map((message, index) => {
              const dateOf = (m: ChatMessageDto) =>
                m.waTimestamp
                  ? new Date(m.waTimestamp * 1000)
                  : new Date(m.creationTime);
              const messageDate = dateOf(message);
              const previous = index > 0 ? messages[index - 1] : null;
              // WhatsApp-style day chip whenever the calendar day changes.
              const showDayDivider =
                !Number.isNaN(messageDate.getTime()) &&
                (!previous ||
                  messageDate.toDateString() !==
                    dateOf(previous).toDateString());
              return (
                <div key={message.id} id={`msg-${message.id}`}>
                  {showDayDivider && (
                    <div className="tw:flex tw:justify-center tw:pb-2 tw:pt-1">
                      <span className="tw:rounded-md tw:border tw:border-border tw:bg-background tw:px-3 tw:py-1 tw:text-xs tw:font-semibold tw:text-muted-foreground">
                        {formatDayDivider(messageDate, t)}
                      </span>
                    </div>
                  )}
                  <MessageBubble
                    message={message}
                    onReact={handleReact}
                    onReply={startReply}
                    contactName={client}
                    quoted={
                      message.replyToWamid
                        ? (byWamid.get(message.replyToWamid) ?? null)
                        : null
                    }
                    onQuoteClick={jumpToMessage}
                    highlighted={highlightId === message.id}
                  />
                </div>
              );
            })}
            {messages.length === 0 && (
              <p className="tw:m-0 tw:py-10 tw:text-center tw:text-sm tw:text-muted-foreground">
                {t("Mandoob:Thread.Empty")}
              </p>
            )}
          </div>
        </div>

        {/* Overlays the message list like WhatsApp: transparent, so scrolled
            messages show through underneath the floating pill. Pointer events
            pass through the empty zones back to the messages. */}
        <footer
          ref={footerRef}
          className="tw:pointer-events-none tw:absolute tw:inset-x-0 tw:bottom-0 tw:px-3 tw:pb-3 tw:z-5"
        >
          <div className="tw:pointer-events-auto">
            {thread.windowOpen && freeMessagesLeft > 0 && (
              <p className="tw:mx-auto tw:mb-1.5 tw:flex tw:w-fit tw:items-center tw:gap-1.5 tw:rounded-full tw:bg-brand-50 tw:px-3 tw:py-1 tw:text-xs tw:font-medium tw:text-brand">
                <Gift className="tw:size-3.5 tw:shrink-0" strokeWidth={2} />
                {t("Mandoob:Offer.FreeLeft", freeMessagesLeft)}
              </p>
            )}
            {thread.windowOpen && cannotAffordMessage ? (
              // Out of credits while billing is on: the composer would only
              // produce 402s, so swap it for the way out (top up), or the
              // website note on native builds that hide purchases.
              <div className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:border tw:border-destructive/30 tw:bg-destructive/5 tw:p-3.5">
                <span className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-background/70 tw:text-destructive">
                  <Coins className="tw:size-4.5" strokeWidth={1.75} />
                </span>
                <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2">
                  <p className="tw:m-0 tw:text-sm tw:leading-relaxed tw:text-foreground/80">
                    {creditsExpired
                      ? t("Mandoob:Credits.ExpiredToast")
                      : t("Mandoob:Thread.NoCreditsBanner")}
                  </p>
                  {paymentsHidden ? (
                    <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                      {t("Mandoob:Home.ManageOnWeb")}
                    </p>
                  ) : (
                    <Button asChild size="sm" className="tw:self-start">
                      <Link to="/plans">{t("Mandoob:Home.BuyCredits")}</Link>
                    </Button>
                  )}
                </div>
              </div>
            ) : thread.windowOpen ? (
              <ChatComposer
                ref={composerRef}
                onSend={handleComposerSend}
                replyTo={replyTo}
                replyToName={client}
                onCancelReply={() => setReplyTo(null)}
                onReplyClick={() => replyTo && jumpToMessage(replyTo.id)}
                deliveryStatus={thread.deliveryStatus}
                onMarkDelivered={toggleDelivered}
                countdown={
                  <SessionCountdown
                    windowOpen={thread.windowOpen}
                    windowExpiresAt={thread.windowExpiresAt}
                    plain
                  />
                }
              />
            ) : (
              // Waiting-for-reply state: informational, not an error — the
              // window opens the moment the client responds.
              <div className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:border tw:border-info/30 tw:bg-info-soft tw:p-3.5">
                <span className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-background/70 tw:text-info">
                  <Hourglass className="tw:size-4.5" strokeWidth={1.75} />
                </span>
                <p className="tw:m-0 tw:text-sm tw:leading-relaxed tw:text-foreground/80">
                  {t("Mandoob:Thread.WindowClosedBanner")}
                </p>
              </div>
            )}
          </div>
        </footer>
      </div>

      <TemplatePickerDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        sending={sendTemplate.isPending}
        onSubmit={(templateId, variables) =>
          sendTemplate.mutate({ conversationId, templateId, variables })
        }
      />

      <DeliveryDetailsSheet
        open={deliveryOpen}
        onClose={() => setDeliveryOpen(false)}
        conversation={thread}
      />
    </div>
  );
}
